import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import ErrorPage from '../../../pages/404Page';
import PositionComponentSkeleton from './PositionComponentSkeleton';
import SEO from '../../SEO/SEO';
import { useCachedFetch } from './../../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../../AnimatedOnScroll';
import { CAREERS_API } from '../../../config/api';
import { getRecaptchaToken } from '../../../utils/recaptcha';
import { useConsent } from '../../../hooks/useConsent';
import { triggerShowBanner } from '../../../components/CookieBanner/CookieBanner';
import './PositionComponent.scss';

const PositionComponent = ({ hideHeaderFooterSetter }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    const { slug } = useParams();

    const { data: positionsData, loading, error } = useCachedFetch('/data/locales/positions.json');

    const { hasConsentForForms } = useConsent();

    const [isLoading, setIsLoading] = useState(true);
    const [hasData, setHasData] = useState(false);

    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isFading, setIsFading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [formKey, setFormKey] = useState(0);

    const fileInputRef = useRef(null);
    const formikRef = useRef(null);

    const currentPosition = useMemo(() => {
        if (!positionsData) return null;
        const vacancies = positionsData.PositionsData?.vacancies || positionsData.vacancies || positionsData.positions || [];
        return vacancies.find(vacancy => vacancy.slug === slug);
    }, [positionsData, slug]);

    const mainCareers = useMemo(() => {
        if (!positionsData) return null;
        return positionsData.PositionsData || positionsData;
    }, [positionsData]);

    const seoData = useMemo(() => {
        if (!currentPosition?.seo) return null;

        const seo = currentPosition.seo;
        const title = seo.title?.[currentLang] || seo.title?.en || currentPosition.positionTitle?.[currentLang] || 'Вакансія | Carabinex';
        const description = seo.description?.[currentLang] || seo.description?.en || '';
        const keywords = seo.keywords?.[currentLang] || seo.keywords?.en || '';
        const type = seo.type || 'product';
        const robots = seo.robots || 'index, follow';

        const robotsParts = robots.split(', ');
        const noIndex = robotsParts.includes('noindex');
        const noFollow = robotsParts.includes('nofollow');

        return { title, description, keywords, type, noIndex, noFollow };
    }, [currentPosition, currentLang]);

    const getLocalizedText = useCallback((text) => {
        if (!text) return '';
        if (typeof text === 'object') {
            return text[currentLang] || text.en || text.ua || '';
        }
        return text;
    }, [currentLang]);

    const errorMessages = useMemo(() => ({
        en: {
            emailLabel: "* Email",
            emailRequired: "Email is required",
            emailInvalid: "Invalid email address",
            emailDomain: "Email must contain a valid domain (e.g. gmail.com)",
            fileTooLarge: "File must not exceed 5 MB",
            fileInvalidType: "Unsupported file type. Allowed: PDF, DOC, DOCX, TXT",
            submitting: "Sending...",
            subscribeError: "Submission error. Please try again.",
            rateLimitError: "Too many requests. Please try again later.",
            consentError: "Consent to personal data processing is required to submit the form",
            keyboardLanguage: "Switch your keyboard",
        },
        ua: {
            emailLabel: "* Email",
            emailRequired: "Email обов'язковий",
            emailInvalid: "Невірна email адреса",
            emailDomain: "Email повинен містити дійсний домен (наприклад, gmail.com)",
            fileTooLarge: "Файл не повинен перевищувати 5 МБ",
            fileInvalidType: "Непідтримуваний тип файлу. Дозволені: PDF, DOC, DOCX, TXT",
            submitting: "Відправка...",
            subscribeError: "Помилка відправки. Спробуйте ще раз.",
            rateLimitError: "Занадто багато запитів. Спробуйте пізніше.",
            consentError: "Для відправки форми необхідна згода на обробку персональних даних",
            keyboardLanguage: "Змініть мову на клавіатурі",
        },
        ru: {
            emailLabel: "* Email",
            emailRequired: "Email обязателен",
            emailInvalid: "Неверный адрес электронной почты",
            emailDomain: "Email должен содержать действительный домен (например, gmail.com)",
            fileTooLarge: "Файл не должен превышать 5 МБ",
            fileInvalidType: "Неподдерживаемый тип файла. Разрешены: PDF, DOC, DOCX, TXT",
            submitting: "Отправка...",
            subscribeError: "Ошибка отправки. Попробуйте еще раз.",
            rateLimitError: "Слишком много запросов. Попробуйте позже.",
            consentError: "Для отправки формы необходимо согласие на обработку персональных данных",
            keyboardLanguage: "Смените язык на клавиатуре",
        },
        pl: {
            emailLabel: "* Email",
            emailRequired: "Email jest wymagany",
            emailInvalid: "Nieprawidłowy adres email",
            emailDomain: "Email musi zawierać prawidłową domenę (np. gmail.com)",
            fileTooLarge: "Plik nie może przekraczać 5 MB",
            fileInvalidType: "Nieobsługiwany typ pliku. Dozwolone: PDF, DOC, DOCX, TXT",
            submitting: "Wysyłanie...",
            subscribeError: "Błąd wysyłania. Spróbuj ponownie.",
            rateLimitError: "Zbyt wiele żądań. Spróbuj później.",
            consentError: "Do wysłania formularza wymagana jest zgoda na przetwarzanie danych osobowych",
            keyboardLanguage: "Zmień język klawiatury",
        },
        de: {
            emailLabel: "* Email",
            emailRequired: "E-Mail ist erforderlich",
            emailInvalid: "Ungültige E-Mail-Adresse",
            emailDomain: "E-Mail muss eine gültige Domain enthalten (z.B. gmail.com)",
            fileTooLarge: "Datei darf 5 MB nicht überschreiten",
            fileInvalidType: "Nicht unterstützter Dateityp. Erlaubt: PDF, DOC, DOCX, TXT",
            submitting: "Senden...",
            subscribeError: "Fehler beim Senden. Bitte versuchen Sie es erneut.",
            rateLimitError: "Zu viele Anfragen. Bitte versuchen Sie es später erneut.",
            consentError: "Für das Absenden des Formulars ist die Zustimmung zur Verarbeitung personenbezogener Daten erforderlich",
            keyboardLanguage: "Tastatursprache wechseln",
        },
        es: {
            emailLabel: "* Email",
            emailRequired: "El correo electrónico es obligatorio",
            emailInvalid: "Dirección de correo electrónico no válida",
            emailDomain: "El correo electrónico debe contener un dominio válido (por ejemplo, gmail.com)",
            fileTooLarge: "El archivo no debe exceder los 5 MB",
            fileInvalidType: "Tipo de archivo no soportado. Permitidos: PDF, DOC, DOCX, TXT",
            submitting: "Enviando...",
            subscribeError: "Error al enviar. Por favor, inténtelo de nuevo.",
            rateLimitError: "Demasiadas solicitudes. Por favor, inténtelo más tarde.",
            consentError: "Para enviar el formulario se requiere el consentimiento para el procesamiento de datos personales",
            keyboardLanguage: "Cambiar idioma del teclado",
        },
    }), []);

    const currentErrors = useMemo(() =>
        errorMessages[currentLang] || errorMessages.en,
        [currentLang, errorMessages]
    );

    const errorMessage = useMemo(() => {
        return mainCareers?.errorMessages?.[currentLang] ||
            mainCareers?.errorMessages?.en ||
            mainCareers?.errorMessages?.ua ||
            'Please select a file';
    }, [mainCareers, currentLang]);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.resetForm();
            formikRef.current.setFieldValue('email', '');
            formikRef.current.setFieldValue('file', null);
        }
        setEmailError("");
        setFormSubmitted(false);
        setIsFading(false);
        setShowError(false);
        setSubmitError('');
    }, [currentLang]);

    useEffect(() => {
        setFormKey(prev => prev + 1);
        setEmailError("");
        setFormSubmitted(false);
        setIsFading(false);
        setShowError(false);
        setSubmitError('');
    }, [currentLang]);

    useEffect(() => {
        if (loading) return;
        if (error) {
            setHasData(false);
            setIsLoading(false);
            return;
        }
        if (currentPosition && currentPosition.positionTitle && currentPosition.content && currentPosition.content.length > 0) {
            setHasData(true);
        } else {
            setHasData(false);
        }
        setIsLoading(false);
    }, [loading, error, currentPosition]);

    const initialValues = useMemo(() => ({
        email: '',
        file: null,
    }), []);

    const validationSchema = useMemo(() =>
        Yup.object({
            email: Yup.string()
                .email(currentErrors.emailInvalid)
                .matches(
                    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                    currentErrors.emailDomain
                )
                .required(currentErrors.emailRequired),
            file: Yup.mixed()
                .nullable()
                .test('fileSize', currentErrors.fileTooLarge, (value) => {
                    if (!value) return true;
                    return value.size <= 5 * 1024 * 1024;
                })
                .test('fileType', currentErrors.fileInvalidType, (value) => {
                    if (!value) return true;
                    const allowedTypes = [
                        'application/pdf',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'text/plain'
                    ];
                    return allowedTypes.includes(value.type);
                }),
        }),
        [currentErrors]
    );

    const handleEmailInput = useCallback((e) => {
        const value = e.target.value;
        if (value && /[А-Яа-яЁё]/.test(value)) {
            setEmailError(currentErrors.keyboardLanguage);
        } else {
            setEmailError('');
        }
        e.target.value = value.replace(/[^A-Za-z0-9@._-]/g, '');
    }, [currentErrors]);

    const formatFileSize = useCallback((bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }, []);

    const handleSubmit = useCallback(async (values, { resetForm, setFieldValue, setFieldError }) => {
        if (!values.file) {
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
            return;
        }

        if (!values.email) {
            setFieldError('email', currentErrors.emailRequired);
            return;
        }

        try {
            await validationSchema.validate({ file: values.file, email: values.email });
        } catch (err) {
            setFieldError('file', err.message);
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
            return;
        }

        if (!hasConsentForForms()) {
            setSubmitError(currentErrors.consentError);
            setFormSubmitted(false);
            setIsSubmitting(false);
            triggerShowBanner();
            setTimeout(() => {
                setSubmitError('');
            }, 10000);
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');
        setShowError(false);

        try {
            let recaptchaToken = '';
            try {
                recaptchaToken = await getRecaptchaToken('careers');
            } catch (err) {
                console.warn('reCAPTCHA error:', err);
            }

            const formData = new FormData();
            formData.append('file', values.file);
            formData.append('position', getLocalizedText(currentPosition?.positionTitle) || '');
            formData.append('email', values.email);
            formData.append('language', currentLang);
            formData.append('recaptchaToken', recaptchaToken);

            const response = await fetch(CAREERS_API, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'form_lead_submitted',
                    form_name: 'cv_form'
                });

                setFormSubmitted(true);
                setSubmitError('');
                setFieldValue('file', null);
                setFieldValue('email', '');
                if (fileInputRef.current) fileInputRef.current.value = '';
                resetForm();

                setTimeout(() => {
                    setIsFading(true);
                    setTimeout(() => {
                        setFormSubmitted(false);
                        setIsFading(false);
                        setIsSubmitting(false);
                    }, 1000);
                }, 3000);
            } else if (response.status === 429) {
                setSubmitError(currentErrors.rateLimitError);
                setFormSubmitted(true);
                setIsSubmitting(false);
                setTimeout(() => {
                    setIsFading(true);
                    setTimeout(() => {
                        setFormSubmitted(false);
                        setIsFading(false);
                        setSubmitError('');
                    }, 1000);
                }, 3000);
            } else {
                const errorMessageText = data.errors?.[0]?.msg || data.message || 'Ошибка отправки';
                setSubmitError(errorMessageText);
                setFormSubmitted(true);
                setIsSubmitting(false);
                setTimeout(() => {
                    setIsFading(true);
                    setTimeout(() => {
                        setFormSubmitted(false);
                        setIsFading(false);
                        setSubmitError('');
                    }, 1000);
                }, 3000);
            }
        } catch (error) {
            console.error('Network error:', error);
            setSubmitError(currentErrors.subscribeError);
            setFormSubmitted(true);
            setIsSubmitting(false);
            setTimeout(() => {
                setIsFading(true);
                setTimeout(() => {
                    setFormSubmitted(false);
                    setIsFading(false);
                    setSubmitError('');
                }, 1000);
            }, 3000);
        }
    }, [currentPosition, getLocalizedText, validationSchema, currentErrors, hasConsentForForms, currentLang]);

    const handleFileChange = useCallback((event, setFieldValue, setFieldError) => {
        const file = event.currentTarget.files[0];
        setFieldValue('file', file);
        setShowError(false);
        setSubmitError('');
        if (file) {
            setFieldError('file', '');
        }
    }, []);

    const renderContentBlock = useCallback((block, index) => {
        if (!block) return null;
        switch (block.type) {
            case 'text':
                return (
                    <p key={index} className='position__description__wrapper__article'>
                        {getLocalizedText(block.content)}
                    </p>
                );
            case 'subtitle':
                return (
                    <h3 key={index} className='position__description__wrapper__subtitle'>
                        {getLocalizedText(block.content)}
                    </h3>
                );
            case 'list':
                return (
                    <ul key={index} className='position__cond__list'>
                        {block.items?.map((item, itemIndex) => (
                            <li key={itemIndex} className='position__cond__list__item'>
                                {getLocalizedText(item)}
                            </li>
                        )) || []}
                    </ul>
                );
            default:
                return null;
        }
    }, [getLocalizedText]);

    if (isLoading) {
        return <PositionComponentSkeleton />;
    }

    if (!hasData || !currentPosition) {
        return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} />;
    }

    return (
        <>
            {seoData && (
                <SEO
                    title={seoData.title}
                    description={seoData.description}
                    keywords={seoData.keywords}
                    type={seoData.type}
                    noIndex={seoData.noIndex}
                    noFollow={seoData.noFollow}
                />
            )}

            <div className='position__wrapper'>
                <div className='position__section__desc__wrapper'>
                    <AnimatedOnScroll animation="fade-up" delay={0}>
                        <p className='position__section__text'>
                            {getLocalizedText(mainCareers?.accent)}
                        </p>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={100}>
                        <h2 className='position__section__title'>
                            {getLocalizedText(currentPosition.positionTitle)}
                        </h2>
                    </AnimatedOnScroll>
                </div>
                <div className='position__description__wrapper'>
                    {currentPosition.content?.map((block, index) => renderContentBlock(block, index))}
                    <Formik
                        key={formKey}
                        innerRef={formikRef}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnBlur={false}
                        validateOnChange={false}
                    >
                        {({ setFieldValue, setFieldError, values, errors, handleSubmit }) => (
                            <Form onSubmit={handleSubmit}>
                                <div className="contact__form__row">
                                    <div className="contact__form__row__item">
                                        <div className="positions__form__input__label">
                                            <Field
                                                name="email"
                                                type="email"
                                                id="email"
                                                placeholder=" "
                                                autoComplete="email"
                                                onInput={(e) => handleEmailInput(e)}
                                            />
                                            <label htmlFor="email">{currentErrors.emailLabel}</label>
                                            {emailError && !values.email && <div className="switch__keyboard__error">{emailError}</div>}
                                            <ErrorMessage name="email" component="div" className="error__message" />
                                        </div>
                                        <div className="contact__form__file__input">
                                            <input
                                                name="file"
                                                type="file"
                                                id="file"
                                                ref={fileInputRef}
                                                onChange={(event) => handleFileChange(event, setFieldValue, setFieldError)}
                                                style={{ display: 'none' }}
                                            />
                                            <label htmlFor="file" className="contact__form__file__label">
                                                {getLocalizedText(currentPosition.buttons?.addButton)}
                                            </label>
                                            {values.file && (
                                                <div className="file__info">
                                                    <span className="file__name">
                                                        📎 {values.file.name}
                                                        <span className="file__size"> ({formatFileSize(values.file.size)})</span>
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="file__remove"
                                                        onClick={() => {
                                                            setFieldValue('file', null);
                                                            setFieldError('file', '');
                                                            if (fileInputRef.current) fileInputRef.current.value = '';
                                                            setShowError(false);
                                                        }}
                                                        aria-label="Удалить файл"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            )}

                                            {errors.file && (
                                                <div className="form__error__message">
                                                    <p>{errors.file}</p>
                                                </div>
                                            )}
                                            {showError && !errors.file && (
                                                <div className="form__error__message">
                                                    <p>{errorMessage}</p>
                                                </div>
                                            )}
                                            {submitError && submitError === currentErrors.consentError && !errors.file && !showError && (
                                                <div className="form__error__message">
                                                    <p>{submitError}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="contact__form__buttons">
                                            {formSubmitted ? (
                                                <div
                                                    className={`form__success__message ${isFading ? 'fade-out' : 'fade-in'}`}
                                                >
                                                    <p>
                                                        {submitError
                                                            ? submitError
                                                            : getLocalizedText(currentPosition.buttons?.success)}
                                                    </p>
                                                </div>
                                            ) : (
                                                <button
                                                    type="submit"
                                                    className="submit__button"
                                                    disabled={isSubmitting}
                                                >
                                                    <span></span>
                                                    <span></span>
                                                    <span></span>
                                                    <span></span>
                                                    {isSubmitting
                                                        ? (currentErrors.submitting || 'Отправка...')
                                                        : getLocalizedText(currentPosition.buttons?.submitButton)
                                                    }
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </>
    );
};

export default React.memo(PositionComponent);